import { useState, useEffect } from "react";
import {
  extractPOBoxNumber,
  separateStreetNumber,
  separateStreetName,
  extractPoBoxNumberFromSelectedAddress,
  getCityFromSelectedAddress,
  getStateFromSelectedAddress,
  getZipCodeFromSelectedAddress,
  getStreetNameFromSelectedAddress,
} from "utils";
import { getBillingAddressSuggest } from "services/get-getBillingAddressSuggest";
import { verifyBillingAddress } from "services/post-verifyBillingAddress";
import { cartState, selectedAddressTypeState, showChangeBillingAddressModalState, toastState } from "atoms";
import { useRecoilState, useSetRecoilState } from "recoil";
import { CheckoutFormModel } from "atoms/models";
import { Spinner } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { PATH } from "utils/const";

export interface ChangeBillingAddressForm {
  formattedAddress: string;
  formattedStreetAddress: string;
  formattedLocationAddress: string;
  addressNumber: string;
}

const ChangeStreetAddress: React.FC<{ setCheckoutForm: Function; checkoutForm: CheckoutFormModel }> = ({ setCheckoutForm, checkoutForm }) => {
  const [spinner, setSpinner] = useState(false);
  const [billingAddresses, setBillingAddresses] = useState<ChangeBillingAddressForm[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedAddress, setSelectedAddress] = useState<any>({});
  const [selectedApartment, setSelectedApartment] = useState("");
  const [toast, setToast] = useRecoilState(toastState);
  const [cart, setCart] = useRecoilState(cartState);
  const [alert, setAlert] = useState({
    alertShow: false,
    alertMessage: "",
  });
  const [buttonText, setButtonText] = useState("Submit");
  const setShowChangeBillingAddressModal = useSetRecoilState(showChangeBillingAddressModalState);
  const setSelectedAddressType = useSetRecoilState(selectedAddressTypeState);
  const location = useLocation();

  useEffect(() => {
    setSelectedAddress({});
    setSelectedApartment("");
    setInputValue("");
    setBillingAddresses([]);
  }, []);

  const resetState = () => {
    setShowChangeBillingAddressModal(false);
    setSelectedAddress({});
    setSelectedApartment("");
    setInputValue("");
    setBillingAddresses([]);
    setButtonText("Submit");
    setAlert({ ...alert, alertMessage: "", alertShow: false });
    setSelectedAddressType("");
  };

  const handleBillingAddressSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (selectedAddress.length === 0) {
      setAlert({ ...alert, alertShow: true, alertMessage: "You must enter and select an address before submitting" });
      return;
    }
    setSpinner(true);
    const response: any = await verifyBillingAddress(selectedAddress, selectedApartment);
    setSpinner(false);

    if (response.status === "success") {
      setCart({ ...cart });
      let streetNumber: string = "";
      let poBox: string = "";

      if (response?.addressVerificationInput?.addressLine1?.includes("Po Box")) {
        poBox = extractPOBoxNumber(response.addressVerificationInput.addressLine1);
      } else {
        streetNumber = response.addressVerificationInput.addressLine1 ? separateStreetNumber(response.addressVerificationInput.addressLine1) : "";
      }
      // Separate the street name
      const streetName = separateStreetName(response.addressVerificationInput.addressLine1);

      setCart({
        ...cart,
        billingStreetNumber: streetNumber ? streetNumber[0] : " ",
        billingStreetName: streetName.includes("Po Box") ? " " : streetName,
        billingCity: response.addressVerificationInput.city,
        billingState: response.addressVerificationInput.state,
        billingZipCode: response.addressVerificationInput.postalCode,
        billingApartmentNumber: selectedApartment,
        billingPOBox: poBox,
      });
      setCheckoutForm({
        ...checkoutForm,
        billingStreetNumber: streetNumber ? streetNumber[0] : " ",
        billingStreetName: streetName.includes("Po Box") ? " " : streetName,
        billingCity: response.addressVerificationInput.city,
        billingState: response.addressVerificationInput.state,
        billingZipCode: response.addressVerificationInput.postalCode,
        billingApartmentNumber: selectedApartment,
        billingPOBox: poBox,
      });
      setShowChangeBillingAddressModal(false);
      setSelectedAddress([]);
      setSelectedApartment("");
      setInputValue("");
      setBillingAddresses([]);
      const toastMessage =
        location.pathname === PATH.CANCEL
          ? "Your billing address has been updated. All future bills will be sent to your new address."
          : "Your billing address has been updated.";
      setToast({
        ...toast,
        show: true,
        variant: "success",
        header: "Success",
        message: toastMessage,
      });
    } else {
      setAlert({
        ...alert,
        alertShow: true,
        alertMessage: "Your address could not be verified with USPS. Please make sure your address is correct before continuing.",
      });
      setButtonText("This address is valid. Please continue");
      return;
    }
  };

  const handleUserVerifiedAddress = async () => {
    setSpinner(true);
    const response: any = await verifyBillingAddress(selectedAddress, selectedApartment);
    setSpinner(false);
    setCart({ ...cart });
    let streetNumber: string | undefined;
    let streetName: string | undefined;
    let poBox: string | undefined;
    let city: string | undefined;
    let state: string | undefined;
    let zipCode: string | undefined;

    if (response?.addressVerificationInput?.addressLine1?.includes("Po Box")) {
      poBox = extractPOBoxNumber(response.addressVerificationInput.addressLine1);
    } else if (response?.addressVerificationInput?.addressLine1) {
      streetNumber = separateStreetNumber(response.addressVerificationInput.addressLine1);
      streetName = separateStreetName(response.addressVerificationInput.addressLine1);
      city = response?.addressVerificationInput?.city;
      state = response?.addressVerificationInput?.state;
      zipCode = response?.addressVerificationInput?.postalCode;
    } else {
      if (selectedAddress.formattedAddress.includes("Po Box")) {
        streetNumber = " ";
        streetName = `PO Box ${selectedAddress.addressNumber}`;
        poBox = extractPoBoxNumberFromSelectedAddress(selectedAddress.formattedAddress);
        city = getCityFromSelectedAddress(selectedAddress.formattedLocationAddress);
        state = getStateFromSelectedAddress(selectedAddress.formattedLocationAddress);
        zipCode = getZipCodeFromSelectedAddress(selectedAddress.formattedLocationAddress);
      } else {
        streetNumber = selectedAddress.addressNumber;
        streetName = getStreetNameFromSelectedAddress(selectedAddress.formattedStreetAddress);
        city = getCityFromSelectedAddress(selectedAddress.formattedLocationAddress);
        state = getStateFromSelectedAddress(selectedAddress.formattedLocationAddress);
        zipCode = getZipCodeFromSelectedAddress(selectedAddress.formattedLocationAddress);
      }
    }

    // Separate the street name
    setCart({
      ...cart,
      billingStreetNumber: streetNumber ? streetNumber[0] : " ",
      billingStreetName: streetName,
      billingCity: city,
      billingState: state,
      billingZipCode: zipCode,
      billingApartmentNumber: selectedApartment,
      billingPOBox: poBox,
    });
    setCheckoutForm({
      ...checkoutForm,
      billingStreetNumber: streetNumber ? streetNumber[0] : " ",
      billingStreetName: streetName,
      billingCity: city,
      billingState: state,
      billingZipCode: zipCode,
      billingApartmentNumber: selectedApartment,
      billingPOBox: poBox,
    });
    resetState();
  };

  const fetchBillingAddresses = async (address: string) => {
    if (address.length < 3) {
      setBillingAddresses([]);
      return;
    }
    setSpinner(true);
    const response = await getBillingAddressSuggest(address);
    setBillingAddresses(response);
    setSpinner(false);
  };

  const handleAddressSelect = (address: any) => {
    setInputValue(address.formattedAddress);
    setBillingAddresses([]);
    setSelectedAddress(address);
  };

  return (
    <div className="change-street-address-container">
      {alert.alertShow ? (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {alert.alertMessage}
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="alert"
            aria-label="Close"
            onClick={() => setAlert({ ...alert, alertShow: false, alertMessage: "" })}
          ></button>
        </div>
      ) : null}
      <div className="row">
        <div className="mb-3 col-md-9">
          <div className="form-floating mb-3">
            <input
              type="text"
              className="form-control"
              id="floatingInput"
              placeholder=""
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                fetchBillingAddresses(e.target.value);
              }}
              onFocus={() => {
                setInputValue("");
                setBillingAddresses([]);
                setSelectedAddress([]);
                setAlert({ ...alert, alertShow: false, alertMessage: "" });
                setButtonText("Submit");
              }}
              autoComplete="off"
            />
            <label htmlFor="floatingInput">Address</label>
          </div>
          {billingAddresses.length > 0 && (
            <ul className="list-group">
              {billingAddresses.map((address: { formattedAddress: string }, index: number) => (
                <li className="list-group-item" key={index} onClick={() => handleAddressSelect(address)}>
                  {address.formattedAddress}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="form-floating mb-3 col-md-3">
          <input
            type="text"
            className="form-control"
            id="floatingInput2"
            placeholder=""
            value={selectedApartment}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedApartment(e.target.value)}
          />
          <label htmlFor="floatingInput2" className="ms-2">
            Apt.
          </label>
        </div>
      </div>
      <div className="billing-address-buttons">
        {spinner ? <Spinner animation="border" className="billing-address-spinner" /> : null}
        <button
          className="btn btn-primary me-1"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            resetState();
          }}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            buttonText === "This address is valid. Please continue" ? handleUserVerifiedAddress() : handleBillingAddressSubmit(e)
          }
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

export default ChangeStreetAddress;
